import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  FileText, Loader2, Upload, FolderOpen, Download, Send, History, CheckCircle2, XCircle, Clock, Trash2,
} from "lucide-react";

const fmtDoc = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return ""; } };
const fmtDateTime = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const kb = (n) => n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;

export const BuildingDocuments = ({ building, residents = [] }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toDel, setToDel] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [sendDoc, setSendDoc] = useState(null);
  const [sendSubject, setSendSubject] = useState("");
  const [sendBody, setSendBody] = useState("");
  const [selIds, setSelIds] = useState([]);
  const [sending, setSending] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingDesc, setPendingDesc] = useState("");
  const [logDoc, setLogDoc] = useState(null);

  const withEmail = residents.filter((r) => (r.email || "").trim());

  const openSend = (doc) => {
    setSendDoc(doc);
    setSendSubject(`${doc.filename} — ${building.name}`);
    const origin = window.location.origin;
    const portalLink = `${origin}/portal`;
    const credLink = `${origin}/richiedi-credenziali/${building.id}`;
    setSendBody(
      `Gentile condòmino,\n\nin allegato trovi il documento "${doc.filename}".\n\n` +
      `I documenti sono disponibili anche sul portale condòmino: ${portalLink}\n` +
      `Se non hai ancora le credenziali di accesso, richiedile qui: ${credLink}\n\n` +
      `Cordiali saluti.`
    );
    setSelIds([]);
  };
  const toggleRecipient = (id) => setSelIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelIds((s) => (s.length === withEmail.length ? [] : withEmail.map((r) => r.id)));

  const sendEmail = async () => {
    if (!selIds.length) return toast.error("Seleziona almeno un destinatario.");
    setSending(true);
    try {
      const r = await api.post(`/buildings/${building.id}/documents/${sendDoc.id}/send`, {
        subject: sendSubject, body: sendBody, recipient_ids: selIds,
      });
      toast.success(`Documento inviato a ${r.data.sent} destinatari${r.data.failed?.length ? `, ${r.data.failed.length} non riusciti` : ""}.`);
      setSendDoc(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Invio non riuscito"); }
    finally { setSending(false); }
  };

  const DOC_ACCEPTED = [".pdf", ".docx", ".xlsx", ".txt", ".csv", ".png", ".jpg", ".jpeg", ".webp"];

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/buildings/${building.id}/documents`); setDocs(r.data || []); }
    catch { /* silenzioso */ }
    finally { setLoading(false); }
  }, [building.id]);

  useEffect(() => { load(); }, [load]);

  const uploadFile = (f) => {
    if (!f) return;
    if (building.subscription_status !== "attivo") {
      return toast.error("Condominio non collegato: attivalo in 'Acquisti e collegamento' per caricare documenti.");
    }
    if (!DOC_ACCEPTED.some((ext) => f.name.toLowerCase().endsWith(ext))) {
      return toast.error("Formato non supportato. Usa PDF, Word, Excel, TXT, CSV o immagini.");
    }
    setPendingFile(f);
    setPendingDesc("");
  };

  const doUpload = async () => {
    if (!pendingFile) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", pendingFile);
    if (pendingDesc.trim()) fd.append("description", pendingDesc.trim());
    try {
      await api.post(`/buildings/${building.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Documento "${pendingFile.name}" caricato e indicizzato per la chat AI`);
      setPendingFile(null); setPendingDesc("");
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Caricamento fallito"); }
    finally { setUploading(false); }
  };

  const upload = (e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadFile(f); };
  const onDrop = (e) => { e.preventDefault(); setDragActive(false); uploadFile(e.dataTransfer?.files?.[0]); };

  const download = async (doc) => {
    try {
      const r = await api.get(`/building-documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Download non riuscito"); }
  };

  const del = async () => {
    if (!toDel) return;
    try {
      await api.delete(`/buildings/${building.id}/documents/${toDel.id}`);
      setDocs((p) => p.filter((x) => x.id !== toDel.id));
      toast.success("Documento eliminato");
      setToDel(null);
    } catch { toast.error("Errore eliminazione"); }
  };

  return (
    <div className="bg-card border border-border rounded-md overflow-hidden relative" data-testid="building-documents"
      onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
      onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
      onDragLeave={(e) => { e.preventDefault(); if (e.currentTarget === e.target) setDragActive(false); }}
      onDrop={onDrop}>
      {dragActive && (
        <div data-testid="doc-drop-overlay" className="absolute inset-0 z-20 bg-[#007BFF]/10 border-2 border-dashed border-[#007BFF] rounded-md flex items-center justify-center pointer-events-none">
          <div className="flex flex-col items-center text-[#007BFF]">
            <Upload className="h-8 w-8 mb-2" />
            <span className="text-sm font-semibold">Rilascia qui il documento</span>
          </div>
        </div>
      )}
      <div className="px-6 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-head text-lg font-semibold flex items-center gap-2"><FolderOpen className="h-5 w-5 text-[#007BFF]" />Area Documentale — {building.name}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Regolamenti, verbali, avvisi e bilanci. La chat AI del portale condòmino risponde leggendo questi documenti.</p>
        </div>
        <label className={building.subscription_status !== "attivo" ? "cursor-not-allowed opacity-40" : "cursor-pointer"}
          title={building.subscription_status !== "attivo" ? "Condominio non collegato: attivalo per caricare documenti" : ""}>
          <input data-testid="doc-upload-input" type="file" className="hidden" accept=".pdf,.docx,.xlsx,.txt,.csv,.png,.jpg,.jpeg,.webp" onChange={upload} disabled={uploading || building.subscription_status !== "attivo"} />
          <span className="inline-flex items-center gap-2 bg-[#007BFF] hover:bg-[#0056B3] text-white rounded-md h-9 px-4 text-sm font-medium">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Carica documento
          </span>
        </label>
      </div>
      {loading ? (
        <div className="p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : docs.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground text-sm">Nessun documento caricato. Trascina qui un file oppure usa "Carica documento" (PDF, Word, Excel o immagini con OCR).</div>
      ) : (
        <div className="divide-y divide-border">
          {docs.map((d) => (
            <div key={d.id} data-testid={`doc-row-${d.id}`} className="flex items-center gap-3 px-6 py-3 hover:bg-secondary/40">
              <FileText className="h-5 w-5 text-[#007BFF] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{d.filename}</div>
                {d.description && <div className="text-[11px] text-[#007BFF] truncate">{d.description}</div>}
                <div className="text-[11px] text-muted-foreground flex items-center gap-2">
                  <span>{kb(d.size || 0)}</span><span>·</span><span>{fmtDoc(d.created_at)}</span>
                  {d.has_text ? <span className="text-emerald-600">· indicizzato AI ✓</span> : <span className="text-amber-600">· testo non estratto</span>}
                </div>
              </div>
              <button data-testid={`doc-send-${d.id}`} onClick={() => openSend(d)} title="Invia via email ai condòmini" className="p-1.5 rounded-md hover:bg-secondary hover:text-[#007BFF] text-muted-foreground"><Send className="h-4 w-4" /></button>
              <button data-testid={`doc-log-${d.id}`} onClick={() => setLogDoc(d)} title="Storico invii" className="relative p-1.5 rounded-md hover:bg-secondary hover:text-[#007BFF] text-muted-foreground">
                <History className="h-4 w-4" />
                {(d.sends || []).length > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[15px] h-[15px] px-0.5 rounded-full bg-[#007BFF] text-white text-[9px] font-bold flex items-center justify-center">{(d.sends || []).length}</span>}
              </button>
              <button data-testid={`doc-download-${d.id}`} onClick={() => download(d)} title="Apri" className="p-1.5 rounded-md hover:bg-secondary hover:text-[#007BFF] text-muted-foreground"><Download className="h-4 w-4" /></button>
              <button data-testid={`doc-delete-${d.id}`} onClick={() => setToDel(d)} title="Elimina" className="p-1.5 rounded-md hover:bg-secondary hover:text-red-600 text-muted-foreground"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
      <AlertDialog open={!!toDel} onOpenChange={(o) => !o && setToDel(null)}>
        <AlertDialogContent data-testid="delete-doc-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il documento?</AlertDialogTitle>
            <AlertDialogDescription><b>{toDel?.filename}</b> non sarà più consultabile dalla chat AI.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-doc" onClick={(e) => { e.preventDefault(); del(); }} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!pendingFile} onOpenChange={(o) => !o && !uploading && setPendingFile(null)}>
        <DialogContent data-testid="doc-upload-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="h-4 w-4 text-[#007BFF]" />Carica documento</DialogTitle>
            <DialogDescription className="truncate">{pendingFile?.name}</DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Cos'è questo documento? (facoltativo)</label>
            <Input data-testid="doc-upload-desc" value={pendingDesc} onChange={(e) => setPendingDesc(e.target.value)}
              placeholder="Es. Verbale assemblea 2025, Preventivo ascensore, Regolamento…" className="h-9 mt-1.5" autoFocus />
            <p className="text-[11px] text-muted-foreground mt-1.5">L'AI indicizza comunque l'intero contenuto del file; la descrizione aiuta a ritrovarlo più facilmente.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingFile(null)} disabled={uploading}>Annulla</Button>
            <Button data-testid="doc-upload-confirm" onClick={doUpload} disabled={uploading} className="bg-[#007BFF] hover:bg-[#0056B3]">
              {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Caricamento…</> : <><Upload className="h-4 w-4 mr-2" />Carica e indicizza</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sendDoc} onOpenChange={(o) => !o && setSendDoc(null)}>
        <DialogContent data-testid="send-doc-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="h-4 w-4 text-[#007BFF]" />Invia documento via email</DialogTitle>
            <DialogDescription className="truncate">{sendDoc?.filename} — {building.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Oggetto</label>
              <Input data-testid="send-doc-subject" value={sendSubject} onChange={(e) => setSendSubject(e.target.value)} className="h-9 mt-1" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Testo del messaggio</label>
              <Textarea data-testid="send-doc-body" value={sendBody} onChange={(e) => setSendBody(e.target.value)} rows={5} className="mt-1 text-sm resize-none" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Destinatari ({selIds.length}/{withEmail.length})</label>
                {withEmail.length > 0 && (
                  <button data-testid="send-doc-toggle-all" onClick={toggleAll} className="text-[11px] text-[#007BFF] hover:underline">
                    {selIds.length === withEmail.length ? "Deseleziona tutti" : "Seleziona tutti"}
                  </button>
                )}
              </div>
              {withEmail.length === 0 ? (
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">Nessun condòmino con email in anagrafica. Aggiungi le email per poter inviare.</p>
              ) : (
                <div className="max-h-48 overflow-y-auto border border-border rounded-md divide-y divide-border" data-testid="send-doc-recipients">
                  {withEmail.map((r) => (
                    <label key={r.id} data-testid={`send-doc-recipient-${r.id}`} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-secondary/50 text-sm">
                      <input type="checkbox" checked={selIds.includes(r.id)} onChange={() => toggleRecipient(r.id)} className="accent-[#007BFF]" />
                      <span className="font-medium">{r.nome}</span>
                      {r.ruolo && <span className="text-[10px] uppercase text-muted-foreground">· {r.ruolo}</span>}
                      <span className="text-xs text-muted-foreground ml-auto truncate">{r.email}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDoc(null)}>Annulla</Button>
            <Button data-testid="send-doc-confirm" onClick={sendEmail} disabled={sending || !selIds.length} className="bg-[#007BFF] hover:bg-[#0056B3]">
              {sending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Invio…</> : <><Send className="h-4 w-4 mr-2" />Invia a {selIds.length || ""}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!logDoc} onOpenChange={(o) => !o && setLogDoc(null)}>
        <DialogContent data-testid="doc-log-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-4 w-4 text-[#007BFF]" />Storico invii</DialogTitle>
            <DialogDescription className="truncate">{logDoc?.filename} — {building.name}</DialogDescription>
          </DialogHeader>
          {(!logDoc?.sends || logDoc.sends.length === 0) ? (
            <div className="py-8 text-center text-sm text-muted-foreground" data-testid="doc-log-empty">
              Questo documento non è ancora stato inviato a nessuno.
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto space-y-3" data-testid="doc-log-list">
              {[...logDoc.sends].reverse().map((s, i) => (
                <div key={i} data-testid={`doc-log-entry-${i}`} className="border border-border rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-xs font-semibold flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-[#007BFF]" />{fmtDateTime(s.ts)}</span>
                    <span className="text-[11px] text-muted-foreground">{s.sent} inviata/e{s.failed ? ` · ${s.failed} non riuscite` : ""}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(s.recipients || []).map((r, k) => (
                      <span key={k} className={`inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5 border ${r.ok === false ? "bg-red-50 border-red-200 text-red-600" : "bg-secondary border-border"}`}>
                        {r.ok === false ? <XCircle className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3 text-emerald-600" />}
                        {r.nome ? `${r.nome} · ` : ""}{r.email}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogDoc(null)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
